import { useState, useMemo } from 'react';
import { useCachedData } from '../hooks/useCachedData';
import { listAllUsers } from '../services/apiService';
import { updateUserInCache } from '../services/cacheService';
import UserTable from '../components/UserTable';
import UserDetail from '../components/UserDetail';
import { Input, Select, Button } from '../components/ui';

interface User {
  userId: string;
  email: string;
  subscription: {
    tier: 'trial' | 'pro';
    status: 'active' | 'cancelled' | 'expired';
    aiLimit: number;
  };
  aiUsed: number;
}

export default function Users() {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [tierFilter, setTierFilter] = useState<'all' | 'trial' | 'pro'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'cancelled' | 'expired'>('all');

  const { data, loading, error, refresh } = useCachedData<{ users: User[] }>(
    'all_users',
    async () => {
      const result = await listAllUsers();
      return result;
    }
  );

  const users = data?.users || [];

  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           user.userId.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesTier = tierFilter === 'all' || user.subscription.tier === tierFilter;
      const matchesStatus = statusFilter === 'all' || user.subscription.status === statusFilter;
      
      return matchesSearch && matchesTier && matchesStatus;
    });
  }, [users, searchTerm, tierFilter, statusFilter]);

  if (loading && !data) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-32 bg-gray-200 rounded animate-pulse" />
        <div className="flex gap-4 mb-4">
          <div className="flex-1 h-10 bg-gray-100 rounded-lg animate-pulse" />
          <div className="h-10 w-28 bg-gray-100 rounded-lg animate-pulse" />
          <div className="h-10 w-28 bg-gray-100 rounded-lg animate-pulse" />
        </div>
        <div className="bg-white rounded-lg border border-neutral-border overflow-hidden">
          <div className="divide-y divide-neutral-border">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="flex gap-4 px-6 py-4">
                <div className="h-4 flex-1 max-w-xs bg-gray-100 rounded animate-pulse" />
                <div className="h-4 w-12 bg-gray-100 rounded animate-pulse" />
                <div className="h-4 w-16 bg-gray-100 rounded animate-pulse" />
                <div className="h-4 w-10 bg-gray-100 rounded animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4">
        <p className="text-sm text-red-800">Error loading users: {error.message}</p>
        <Button variant="danger" onClick={() => refresh()} className="mt-3">
          Retry
        </Button>
      </div>
    );
  }

  if (selectedUser) {
    return (
      <UserDetail
        user={selectedUser}
        onClose={() => setSelectedUser(null)}
        onUpdate={async (updatedUser) => {
          await updateUserInCache(updatedUser.userId, updatedUser);
          await refresh();
          setSelectedUser(updatedUser);
        }}
      />
    );
  }

  const tierFilterOptions = [
    { value: 'all', label: 'All Tiers' },
    { value: 'trial', label: 'Trial' },
    { value: 'pro', label: 'Pro' },
  ];
  const statusFilterOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'expired', label: 'Expired' },
  ];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-neutral-primary">Users</h2>

      <div className="rounded-2xl border border-neutral-border bg-white p-5 shadow-card">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <Input
              type="search"
              placeholder="Search by email or user ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
          <Select
            options={tierFilterOptions}
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value as 'all' | 'trial' | 'pro')}
            className="w-40"
          />
          <Select
            options={statusFilterOptions}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'cancelled' | 'expired')}
            className="w-40"
          />
        </div>
        <p className="mt-3 text-sm text-neutral-secondary">
          Showing {filteredUsers.length} of {users.length} users
        </p>
      </div>

      <UserTable
        users={filteredUsers}
        onUserClick={(user) => setSelectedUser(user)}
      />
    </div>
  );
}
